import { trackItems } from '../analytics/analytics.js';
// src/pages/CartPage.jsx
// Migrated from prototype/between-the-lines-v2.html lines 9375-9467.
// Modifications:
//   - 3 setPage calls converted to navigate
//   - 1 setActivePack call commented out with Sprint 2d TODO note
//   - setActivePack prop dropped (will rewire in Sprint 2d)

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IconArrowRight, IconBookOpen, IconCart } from '../components/icons.jsx';
import { DEFAULT_BASKET } from '../config/basket.js';
import { getIntervalDescription, getIntervalSuffix } from '../config/intervalOptions.js';

export const CartPage = ({ state }) => {
    const navigate = useNavigate();
    const basket = { ...DEFAULT_BASKET, ...(state.basketContent || {}) };
    const removeFromCart = (id) => {
        trackItems('remove_from_cart', state.cart.filter(i => i.id === id));
        state.setCart(state.cart.filter(i => i.id !== id));
    };
    const total = state.cart.reduce((sum, item) => sum + item.price, 0);

    return (
        <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-charcoal-bg paper-texture page-root-text-charcoal">
            <div className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-16">
                <div className="mb-10">
                    <div className="font-display text-charcoal-accent text-xs mb-2 font-semibold">{basket.pageTagline}</div>
                    <h1 className="font-display text-3xl md:text-5xl text-charcoal-text">{basket.pageTitle}</h1>
                </div>

                {state.cart.length === 0 ? (
                    <div className="border border-charcoal-accent/20 bg-charcoal-page/60 p-12 text-center decorative-corner text-charcoal-accent">
                        <IconCart className="w-12 h-12 text-charcoal-accent/40 mx-auto mb-4" />
                        <p className="font-serif italic text-charcoal-muted text-lg mb-6">{basket.emptyMessage}</p>
                        <button onClick={() => navigate('/dashboard')}
                            className="inline-flex items-center gap-3 px-5 py-3 border border-charcoal-accent text-charcoal-accent font-display text-xs hover:bg-charcoal-accent hover:text-charcoal-pageDark transition-colors font-semibold">
                            Browse Study Packs <IconArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="border border-charcoal-accent/20 bg-charcoal-page/60 mb-6 text-charcoal-accent">
                            {state.cart.map((item, idx) => (
                                <div key={item.id} className={`p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center ${idx > 0 ? 'border-t border-charcoal-accent/15' : ''}`}>
                                    <div className="w-14 h-14 bg-charcoal-accent/10 border border-charcoal-accent/30 flex items-center justify-center flex-shrink-0">
                                        <IconBookOpen className="w-6 h-6 text-charcoal-accent" />
                                    </div>
                                    <div className="flex-grow min-w-0">
                                        {item.type === 'credit-pack' ? (
                                            <>
                                                <div className="font-display text-charcoal-accent text-xs mb-1 font-semibold">Tutor Review Credits</div>
                                                <div className="font-display text-charcoal-text text-sm">{item.label}</div>
                                                <div className="font-serif italic text-charcoal-muted text-xs">+{item.credits} review credit{item.credits === 1 ? '' : 's'}</div>
                                                <div className="font-serif italic text-charcoal-accent/80 text-xs mt-1">One-time purchase · permanent · never expires</div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="btl-exam-code font-display text-charcoal-accent text-xs tracking-wide uppercase mb-1 font-semibold">{item.paper}</div>
                                                <div className="font-display text-charcoal-text text-sm">{item.label}</div>
                                                <div className="font-serif italic text-charcoal-muted text-xs">{item.description}</div>
                                                <div className="font-serif italic text-charcoal-accent/80 text-xs mt-1">{getIntervalDescription(item.autoRenewInterval)} · HK${item.price}{getIntervalSuffix(item.autoRenewInterval)}</div>
                                            </>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="font-display text-charcoal-accent text-lg">HK${item.price}</span>
                                        <button onClick={() => removeFromCart(item.id)} className="text-charcoal-muted hover:text-red-400 transition-colors text-xs font-display font-semibold">
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="border border-charcoal-accent/30 bg-charcoal-pageDark p-6 decorative-corner text-charcoal-accent">
                            <div className="space-y-2 mb-5">
                                <div className="flex justify-between text-sm font-serif">
                                    <span className="text-charcoal-muted">{basket.subtotalLabel}</span>
                                    <span className="text-charcoal-text">HK${total}</span>
                                </div>
                                <div className="flex justify-between text-sm font-serif">
                                    <span className="text-charcoal-muted">{basket.processingLabel}</span>
                                    <span className="text-charcoal-muted italic">{basket.processingValue}</span>
                                </div>
                            </div>
                            <div className="flex justify-between items-center pb-5 mb-5 border-t border-charcoal-accent/30 pt-4">
                                <span className="font-display text-charcoal-text text-sm font-semibold">{basket.totalLabel}</span>
                                <span className="font-display text-charcoal-accent text-3xl">HK${total}</span>
                            </div>
                            <button onClick={() => {
                                if (state.cart.length === 0) return;
                                // WP-3: cart -> unified checkout (method selector lives there:
                                // Stripe card/apple/google + manual PayMe/FPS).
                                navigate('/checkout');
                            }}
                                className="w-full py-4 bg-charcoal-accent text-charcoal-pageDark font-display text-sm hover:bg-charcoal-accentLight transition-colors btn-sweep font-semibold">
                                {basket.checkoutButton}
                            </button>
                            <p className="mt-3 text-charcoal-muted font-serif italic text-xs leading-relaxed">
                                One payment. 12 months of access. No automatic renewal. Pay by card through Stripe or use PayMe/FPS.
                            </p>
                            <button onClick={() => navigate('/dashboard')} className="w-full mt-3 text-charcoal-muted hover:text-charcoal-accent font-serif italic text-sm transition-colors">
                                ← Continue browsing
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CartPage;
